import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'
import { Link, useLocation, useRouter } from '@tanstack/react-router'
import { AutocompleteCustomerContainer } from '@/components/AutocompleteCustomerContainer'
import { isAuthRejection } from '@/db/application/auth'
import { JOB_BOARD_COLUMNS } from '@/db/domain/jobBoard'
import { isReadyForCollection } from '@/db/domain/jobStatus'
import type PocketBase from 'pocketbase'
import type { ReactNode } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import type { SearchOption } from '@/components/ui/Search'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import { useDb } from '@/lib/dbContext'
import { useAuth } from '@/lib/auth'
import { useBranding } from '@/lib/branding'
import { KIOSK_CLIENT_ORIGIN } from '@/db/clientOrigin'
import type {
  Document,
  Kiosk,
  KioskSession,
  Job,
  Customer,
} from '@/lib/schemas/db'
import {
  KIOSK_DEVICE_TYPE_OPTIONS,
  KIOSK_STEP_CUSTOMER,
  KIOSK_STEP_DEVICE,
  KIOSK_STEP_TERMS,
  KIOSK_STEP_TITLES,
  type KioskSessionFormData,
  type KioskSessionPeripherals,
  type KioskStep,
} from '@/lib/schemas/kiosk'
import {
  findJobNumberTagConflict,
  isPocketBaseJobNumberTagUniqueError,
  normalizeJobNumberTag,
  type JobNumberTagConflict,
} from '@/lib/jobNumberTag'
import { appVersion, fetchServedAppVersion } from '@/lib/buildInfo'
import { getOrCreateKioskDeviceUuid } from '@/lib/kioskDevice'
import { ServicesScreen, type ServiceItem } from '@/screens/ServicesScreen'
import { useServiceMutations } from '@/screens/ServicesScreenContainer'
import { useServiceItems } from '@/screens/useServiceItems'
import type { LedgerColumn } from '@/components/ui/LedgerTable'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'

const CHECKIN_TERMS_KEY = 'checkin_terms'
const CHECKOUT_TERMS_KEY = 'check_out_terms'
const THANK_YOU_DURATION_MS = 5000
const KIOSK_FORM_SYNC_DEBOUNCE_MS = 250

function normaliseOptional(value?: string) {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 ? trimmed : undefined
}

function normaliseArray(values: KioskSessionPeripherals[]) {
  return values.length > 0 ? values : undefined
}

function getStepValidity(step: KioskStep, formData: KioskSessionFormData) {
  switch (step) {
    case KIOSK_STEP_CUSTOMER:
      return Boolean(formData.fullName?.trim() && formData.contactPhone?.trim())
    case KIOSK_STEP_DEVICE: {
      const hasType = Boolean(
        formData.deviceType?.trim() &&
          (formData.deviceType !== 'other' || formData.deviceTypeOther?.trim()),
      )
      const requiresDescription = formData.deviceType === 'Multiple Devices'
      const hasDescription = Boolean(formData.deviceDescription?.trim())
      return hasType && Boolean(formData.fault?.trim()) && (!requiresDescription || hasDescription)
    }
    case KIOSK_STEP_TERMS:
      return true
    default:
      return false
  }
}

function dataUrlToFile(dataUrl: string, filename: string) {
  const [meta, base64] = dataUrl.split(',')
  if (!meta || !base64) {
    throw new Error('Invalid signature data format')
  }
  const mimeMatch = meta.match(/data:(.*?);base64/)
  const mime = mimeMatch?.[1] ?? 'image/png'
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new File([bytes], filename, { type: mime || 'image/png' })
}


function renderSimpleMarkdown(markdown?: string) {
  const source = markdown?.trim()
  if (!source) return 'Terms document not found.'

  const lines = source.split(/\r?\n/)
  const blocks: ReactNode[] = []
  let paragraph: string[] = []
  let listItems: string[] = []
  let listKey = 0

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    blocks.push(<p key={`p-${blocks.length}`}>{paragraph.join(' ')}</p>)
    paragraph = []
  }

  const flushList = () => {
    if (listItems.length === 0) return
    const items = [...listItems]
    blocks.push(
      <ul key={`ul-${listKey++}`}>
        {items.map((item, index) => <li key={`li-${index}`}>{item}</li>)}
      </ul>,
    )
    listItems = []
  }

  const inline = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>
      }
      return <span key={index}>{part}</span>
    })
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()

    if (!line) {
      flushParagraph()
      flushList()
      continue
    }

    if (line.startsWith('# ')) {
      flushParagraph()
      flushList()
      blocks.push(<h1 key={`h1-${blocks.length}`}>{inline(line.slice(2).trim())}</h1>)
      continue
    }

    if (line.startsWith('## ')) {
      flushParagraph()
      flushList()
      blocks.push(<h2 key={`h2-${blocks.length}`}>{inline(line.slice(3).trim())}</h2>)
      continue
    }

    if (line.startsWith('- ')) {
      flushParagraph()
      listItems.push(line.slice(2).trim())
      continue
    }

    flushList()
    paragraph.push(line)
  }

  flushParagraph()
  flushList()

  return blocks
}

function usePocketBase() {
  const db = useDb() as ReturnType<typeof useDb> & { pb?: PocketBase }
  return db.pb ?? null
}

async function refreshKioskPairedDevices(
  collection: ({ utils?: { refetch?: () => Promise<unknown> }; preload?: () => Promise<unknown> } & Record<string, unknown>) | null | undefined,
) {
  if (collection?.utils?.refetch) {
    await collection.utils.refetch()
    return
  }

  await collection?.preload?.()
}

function useKioskData() {
  const db = useDb() as ReturnType<typeof useDb> & { kioskPairedDevicesCollection?: unknown }
  const kiosks = useLiveQuery((q) => q.from({ kiosk: db.kiosksCollection }))
  const sessions = useLiveQuery((q) => q.from({ session: db.kioskSessionsCollection }))
  const documents = useLiveQuery((q) => q.from({ document: db.documentsCollection }))
  const pairedDevices = db.kioskPairedDevicesCollection
    ? useLiveQuery((q) => q.from({ device: db.kioskPairedDevicesCollection as never }))
    : null

  return {
    kiosks,
    sessions,
    documents,
    pairedDevices,
  }
}

function useCustomersSnapshot() {
  const db = useDb()
  return useLiveQuery((q) => q.from({ customer: db.customersCollection }))
}

function KioskLoadingState({ fullScreen = false }: { fullScreen?: boolean }) {
  return (
    <div className={fullScreen ? 'absolute inset-0 flex overflow-hidden bg-slate-950 text-white' : 'rounded-[2rem] border border-slate-200 bg-white p-8 text-slate-600'}>
      <div className="flex h-full w-full items-center justify-center">Loading kiosk…</div>
    </div>
  )
}

function KioskErrorState({ message, fullScreen = false }: { message: string; fullScreen?: boolean }) {
  return (
    <div className={fullScreen ? 'absolute inset-0 flex overflow-hidden bg-slate-950 p-8 text-white' : 'rounded-[2rem] border border-rose-200 bg-rose-50 p-8 text-rose-700'}>
      <div className="flex h-full w-full items-center justify-center">
        <p>{message}</p>
      </div>
    </div>
  )
}

function KioskEmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white p-8 text-slate-600">
      <div className="flex h-full w-full items-center justify-center">
        <p>{message}</p>
      </div>
    </div>
  )
}

function StepProgress({ currentStep, inverted = false }: { currentStep: number; inverted?: boolean }) {
  return (
    <div className="flex items-center justify-center gap-3">
      {KIOSK_STEP_TITLES.map((title, index) => {
        const active = currentStep === index
        const done = currentStep > index
        const base = inverted
          ? active
            ? 'bg-white text-slate-950'
            : done
              ? 'bg-emerald-400 text-slate-950'
              : 'border border-white/30 text-white/70'
          : active
            ? 'bg-white text-slate-950'
            : done
              ? 'bg-emerald-400 text-slate-950'
              : 'border border-white/20 text-slate-300'

        return (
          <div key={title} className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${base}`}>
            <span>{index + 1}</span>
            <span className="hidden md:inline">{title}</span>
          </div>
        )
      })}
    </div>
  )
}

function KioskWelcomeScreen(props: {
  label?: string
  friendlyName?: string
  sessionBadge?: string
  fullScreen?: boolean
  subtitle?: string
}) {
  const brand = useBranding()
  const subtitle = props.subtitle ?? (props.label
    ? `Please see the receptionist to get started on ${props.label}.`
    : 'Please see the receptionist to get started.')

  return (
    <div className={props.fullScreen ? 'absolute inset-0 flex overflow-hidden bg-linear-to-br from-slate-950 via-slate-900 to-slate-800 px-8 text-center text-white' : 'relative flex h-full flex-col items-center justify-center rounded-3xl border border-dashed border-white/20 bg-white/5 p-10 text-center backdrop-blur'}>
      <div className="flex h-full w-full flex-col items-center justify-center">
        {props.friendlyName ? (
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            <span className="rounded-full border border-white/15 bg-white/10 px-4 py-1 text-sm font-medium text-white/90">
              {props.friendlyName}
            </span>
          </div>
        ) : null}
        {brand.logoUrl ? (
          <img
            src={brand.logoUrl}
            alt={brand.name}
            className={props.fullScreen ? 'mb-10 h-32 w-auto max-w-[85%]' : 'mb-6 h-16 w-auto max-w-[85%]'}
          />
        ) : (
          <p className={props.fullScreen ? 'mb-10 text-6xl font-bold uppercase tracking-wide' : 'mb-6 text-3xl font-bold uppercase tracking-wide'}>
            {brand.name}
          </p>
        )}
        <h2 className={props.fullScreen ? 'mt-4 text-5xl font-semibold tracking-tight' : 'mt-3 text-3xl font-semibold text-white'}>Welcome</h2>
        <p className={props.fullScreen ? 'mt-6 max-w-2xl text-xl leading-8 text-slate-200' : 'mt-3 max-w-md text-sm leading-6 text-slate-300'}>
          {subtitle}
        </p>
      </div>
      {props.sessionBadge ? (
        <div className="absolute inset-x-0 bottom-5 flex justify-center px-6">
          <div className="font-mono text-xs font-medium tracking-[0.2em] text-white/70">
            {props.sessionBadge}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function KioskThankYouScreen() {
  return (
    <div className="absolute inset-0 flex overflow-hidden bg-emerald-950 px-8 text-center text-white">
      <div className="flex h-full w-full flex-col items-center justify-center">
        <div className="rounded-full bg-white/10 px-5 py-2 text-sm font-medium text-emerald-100">Check-in complete</div>
        <h1 className="mt-6 text-5xl font-semibold tracking-tight">Thank you</h1>
        <p className="mt-6 max-w-2xl text-xl leading-8 text-emerald-50">
          Your device has been checked in. The receptionist will assist you shortly.
        </p>
      </div>
    </div>
  )
}

function KioskCheckoutThankYouScreen() {
  return (
    <div className="absolute inset-0 flex overflow-hidden bg-emerald-950 px-8 text-center text-white">
      <div className="flex h-full w-full flex-col items-center justify-center">
        <div className="rounded-full bg-white/10 px-5 py-2 text-sm font-medium text-emerald-100">Check-out complete</div>
        <h1 className="mt-6 text-5xl font-semibold tracking-tight">Thank you</h1>
        <p className="mt-6 max-w-2xl text-xl leading-8 text-emerald-50">
          Collection details have been recorded. The receptionist will finish the handoff shortly.
        </p>
      </div>
    </div>
  )
}

function KioskJobCreatedPanel(props: {
  kiosk: Kiosk
  session: KioskSession
  onAcknowledge: () => Promise<void>
  onAssignTag: (tag: string) => Promise<void>
}) {
  const db = useDb()
  const [tagInput, setTagInput] = useState('')
  const [isAssigning, setIsAssigning] = useState(false)
  const [justAssigned, setJustAssigned] = useState(false)
  const [pendingAssignedTag, setPendingAssignedTag] = useState<string | null>(null)
  const [confirmedAssignedTag, setConfirmedAssignedTag] = useState<string | null>(null)
  const [assignError, setAssignError] = useState<string | null>(null)

  const jobsResult = useLiveQuery((q) => q.from({ job: db.jobsCollection }))
  const job = props.session.jobId
    ? (jobsResult.data ?? []).find((entry) => entry.id === props.session.jobId)
    : undefined
  const jobPublicId = job?.publicId?.trim() || null

  const currentTag = job?.numberTag?.trim() ?? ''
  const effectiveTag = currentTag || confirmedAssignedTag || ''
  const hasTag = effectiveTag !== ''

  // Prefill the input with the job's existing tag once it loads, without
  // clobbering anything the receptionist starts typing.
  const prefilledRef = useRef(false)
  useEffect(() => {
    if (!prefilledRef.current && effectiveTag) {
      prefilledRef.current = true
      setTagInput(effectiveTag)
    }
  }, [effectiveTag])

  const trimmedTag = tagInput.trim()
  const canAssign = Boolean(props.session.jobId) && trimmedTag !== '' && !isAssigning
  const canAcknowledge = hasTag

  useEffect(() => {
    if (!pendingAssignedTag) return
    if (currentTag === pendingAssignedTag) {
      setConfirmedAssignedTag(currentTag)
      setJustAssigned(true)
      setPendingAssignedTag(null)
    }
  }, [currentTag, pendingAssignedTag])

  const jobDetailsPath = props.session.jobId ? `/jobs/${props.session.jobId}` : null
  const jobDetailsUrl =
    jobDetailsPath === null
      ? null
      : typeof window === 'undefined'
        ? jobDetailsPath
        : new URL(jobDetailsPath, window.location.origin).toString()

  const handleAssign = async () => {
    if (!props.session.jobId || trimmedTag === '' || isAssigning) return
    setIsAssigning(true)
    setAssignError(null)
    setJustAssigned(false)
    setPendingAssignedTag(trimmedTag)
    try {
      await props.onAssignTag(trimmedTag)
      setConfirmedAssignedTag(trimmedTag)
      setJustAssigned(true)
      setPendingAssignedTag(null)
    } catch (error) {
      setPendingAssignedTag(null)
      setAssignError(error instanceof Error ? error.message : 'Could not assign the tag. Please try again.')
    } finally {
      setIsAssigning(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <PanelShell
        title={getKioskDisplayName(props.kiosk)}
        subtitle="The customer has completed check-in on the tablet."
        light
      >
        <div className="flex h-full min-h-[24rem] flex-col items-center justify-center rounded-3xl border border-emerald-200 bg-linear-to-br from-emerald-50 to-white p-10 text-center">
          <div className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-900">
            New job created
          </div>
          <h2 className="mt-5 text-3xl font-semibold text-slate-900">Finish up</h2>
          <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
            The tablet has finished the check-in flow and created a job. You can continue in the normal job workflow now.
          </p>
          {jobDetailsUrl ? (
            <a
              href={jobDetailsUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-baseline gap-1 text-2xl font-semibold text-blue-700 hover:text-blue-800 hover:underline"
            >
              Jobs / {jobPublicId ?? 'Pending…'}
              <span className="text-base">↗</span>
            </a>
          ) : null}
          {job?.checkedInNoSignatureReason ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span className="font-medium">No signature reason:</span> {job.checkedInNoSignatureReason}
            </div>
          ) : null}

          {props.session.jobId ? (
            <div className="mt-8 w-full max-w-sm space-y-4 text-left">
              <div>
                <FormCapsLabel>{hasTag ? 'Tag' : 'Assign tag'}</FormCapsLabel>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  {hasTag
                    ? 'This job already has a tag. Change it to reassign.'
                    : 'Enter the physical tag number and move the job to Ready to Work.'}
                </p>
                <div className="mt-3 flex items-end gap-3">
                  <div className="flex-1">
                    <UnderlineInput
                      value={tagInput}
                      onChange={(event) => {
                        setJustAssigned(false)
                        setTagInput(event.target.value)
                      }}
                      placeholder="Tag number"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void handleAssign()
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    disabled={!canAssign}
                    onClick={() => void handleAssign()}
                    className={
                      canAssign
                        ? 'rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700'
                        : 'rounded-xl bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-400'
                    }
                  >
                    {isAssigning ? 'Saving…' : hasTag ? 'Reassign tag' : 'Assign & start'}
                  </button>
                </div>
                {assignError ? (
                  <p className="mt-2 text-xs text-red-600">{assignError}</p>
                ) : null}
                {justAssigned && !assignError && !pendingAssignedTag ? (
                  <p className="mt-2 text-xs font-medium text-emerald-700">
                    Tag <span className="font-semibold">{effectiveTag}</span> assigned · moved to Ready to Work.
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                disabled={!canAcknowledge}
                className={
                  canAcknowledge
                    ? 'w-full rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800'
                    : 'w-full rounded-2xl bg-slate-200 px-5 py-3 text-sm font-semibold text-slate-400'
                }
                onClick={() => {
                  if (!canAcknowledge) return
                  void props.onAcknowledge()
                }}
              >
                Finish check-in
              </button>
            </div>
          ) : null}
        </div>
      </PanelShell>
    </div>
  )
}

function PanelShell(props: {
  title: string
  subtitle: string
  children: ReactNode
  actions?: ReactNode
  light?: boolean
}) {
  const { title, subtitle, children, actions, light = false } = props
  return (
    <section
      className={`relative isolate flex h-full min-h-0 flex-col overflow-hidden rounded-[2rem] p-6 ${
        light
          ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
          : 'bg-slate-950 text-white ring-1 ring-white/10'
      }`}
    >
      <header className="mb-4 flex items-start justify-between gap-6">
        <div>
          <h2 className={light ? 'text-3xl font-semibold text-slate-900' : 'text-3xl font-semibold text-white'}>{title}</h2>
          <p className={light ? 'mt-2 text-sm text-slate-500' : 'mt-2 text-sm text-slate-300'}>{subtitle}</p>
        </div>
        {actions}
      </header>
      <div className="min-h-0 flex-1">{children}</div>
    </section>
  )
}

function CustomerFields(props: {
  formData: KioskSessionFormData
  onChange: (patch: Partial<KioskSessionFormData>) => void
  inverted?: boolean
}) {
  const { formData, onChange, inverted = false } = props
  const inputClass = inverted ? 'text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]' : undefined

  return (
    <div className="grid h-full gap-6 md:grid-cols-2">
      <div className="grid content-start gap-4">
        <FieldBlock label="Full name" inverted={inverted}>
          <UnderlineInput className={inputClass} value={formData.fullName ?? ''} onChange={(event) => onChange({ fullName: event.target.value })} placeholder="Customer full name" />
        </FieldBlock>
        <FieldBlock label="Contact phone" inverted={inverted}>
          <UnderlineInput className={inputClass} value={formData.contactPhone ?? ''} onChange={(event) => onChange({ contactPhone: event.target.value })} placeholder="Primary contact number" />
        </FieldBlock>
        <FieldBlock label="Contact email" inverted={inverted}>
          <UnderlineInput className={inputClass} value={formData.contactEmail ?? ''} onChange={(event) => onChange({ contactEmail: event.target.value })} />
        </FieldBlock>
        <FieldBlock label="Contact address" inverted={inverted}>
          <UnderlineTextarea className={inputClass} rows={3} value={formData.contactAddress ?? ''} onChange={(event) => onChange({ contactAddress: event.target.value })} />
        </FieldBlock>
      </div>
      <div className="grid content-start gap-4">
        <FieldBlock label="Invoice name" inverted={inverted}>
          <UnderlineInput className={inputClass} value={formData.invoiceName ?? ''} onChange={(event) => onChange({ invoiceName: event.target.value })} />
        </FieldBlock>
        <FieldBlock label="Invoice phone" inverted={inverted}>
          <UnderlineInput className={inputClass} value={formData.invoicePhone ?? ''} onChange={(event) => onChange({ invoicePhone: event.target.value })} />
        </FieldBlock>
        <FieldBlock label="Invoice email" inverted={inverted}>
          <UnderlineInput className={inputClass} value={formData.invoiceEmail ?? ''} onChange={(event) => onChange({ invoiceEmail: event.target.value })} />
        </FieldBlock>
        <FieldBlock label="Invoice VAT" inverted={inverted}>
          <UnderlineInput className={inputClass} value={formData.invoiceVat ?? ''} onChange={(event) => onChange({ invoiceVat: event.target.value })} />
        </FieldBlock>
        <FieldBlock label="Invoice address" inverted={inverted}>
          <UnderlineTextarea className={inputClass} rows={3} value={formData.invoiceAddress ?? ''} onChange={(event) => onChange({ invoiceAddress: event.target.value })} />
        </FieldBlock>
      </div>
    </div>
  )
}

function SelectField(props: {
  label: string
  value: string
  options: readonly string[]
  onChange: (value: string) => void
  otherValue?: string
  onOtherChange?: (value: string) => void
  inverted?: boolean
}) {
  const otherSelected = props.value.toLowerCase() === 'other'
  return (
    <div className="space-y-2">
      <FormFieldLabel className={props.inverted ? 'text-slate-200' : undefined}>{props.label}</FormFieldLabel>
      <select
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className={props.inverted ? 'h-11 w-full rounded-xl border border-white/15 bg-slate-900 px-3 text-sm text-white outline-none focus:border-white/40' : 'h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-slate-500'}
      >
        <option value="">Select…</option>
        {props.options.map((option) => {
          const value = option.toLowerCase() === 'other' ? 'other' : option
          return (
            <option key={option} value={value}>
              {option}
            </option>
          )
        })}
      </select>
      {otherSelected ? (
        <UnderlineInput
          className={props.inverted ? 'text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]' : undefined}
          value={props.otherValue ?? ''}
          onChange={(event) => props.onOtherChange?.(event.target.value)}
          placeholder={`Enter ${props.label.toLowerCase()}`}
        />
      ) : null}
    </div>
  )
}

function DeviceFields(props: {
  formData: KioskSessionFormData
  onChange: (patch: Partial<KioskSessionFormData>) => void
  inverted?: boolean
}) {
  const { formData, onChange, inverted = false } = props
  const [showPassword, setShowPassword] = useState(true)
  const peripherals = formData.peripherals ?? []

  const togglePeripheral = (value: KioskSessionPeripherals) => {
    const next = peripherals.includes(value)
      ? peripherals.filter((entry) => entry !== value)
      : [...peripherals, value]

    onChange({
      peripherals: normaliseArray(next),
      peripheralsOther: next.includes('other') ? formData.peripheralsOther : undefined,
    })
  }

  return (
    <div className="grid h-full gap-5 md:grid-cols-2">
      <div className="grid content-start gap-4">
        <SelectField
          label="Type"
          value={formData.deviceType ?? ''}
          options={KIOSK_DEVICE_TYPE_OPTIONS}
          otherValue={formData.deviceTypeOther}
          onChange={(value) => onChange({ deviceType: value, deviceTypeOther: value === 'other' ? formData.deviceTypeOther : undefined })}
          onOtherChange={(value) => onChange({ deviceTypeOther: value })}
          inverted={inverted}
        />
        <FieldBlock label="Device(s) description" inverted={inverted}>
          <UnderlineInput
            className={inverted ? 'text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]' : undefined}
            value={formData.deviceDescription ?? ''}
            onChange={(event) => onChange({ deviceDescription: event.target.value })}
            placeholder="Brand, model, quantity, or identifying notes"
          />
        </FieldBlock>
        <div className="space-y-2">
          <FormFieldLabel className={inverted ? 'text-slate-200' : undefined}>Device password</FormFieldLabel>
          <div className="flex items-center gap-3">
            <UnderlineInput
              type={showPassword ? 'text' : 'password'}
              value={formData.devicePassword ?? ''}
              onChange={(event) => onChange({ devicePassword: event.target.value })}
              placeholder="Leave blank if none"
              className={inverted ? 'flex-1 text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]' : 'flex-1'}
            />
            <button type="button" className={inverted ? 'rounded-lg border border-white/20 px-3 py-2 text-xs font-medium text-slate-100 hover:bg-white/10' : 'rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50'} onClick={() => setShowPassword((current) => !current)}>
              {showPassword ? 'Mask' : 'Show'}
            </button>
          </div>
          <p className={inverted ? 'text-xs text-slate-300' : 'text-xs text-slate-500'}>Shown by default so the receptionist can double-check accuracy.</p>
        </div>
      </div>
      <div className="grid content-start gap-4">
        <FieldBlock label="Fault" inverted={inverted}>
          <UnderlineTextarea className={inverted ? 'text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]' : undefined} rows={5} value={formData.fault ?? ''} onChange={(event) => onChange({ fault: event.target.value })} placeholder="Describe the issue" />
        </FieldBlock>
        <div className="space-y-3">
          <FormFieldLabel className={inverted ? 'text-slate-200' : undefined}>Peripherals</FormFieldLabel>
          <div className="grid grid-cols-2 gap-3">
            {[
              ['charger', 'Charger'],
              ['mouse', 'Mouse'],
              ['bag', 'Bag'],
              ['other', 'Other'],
            ].map(([value, label]) => {
              const checked = peripherals.includes(value as KioskSessionPeripherals)
              return (
                <label key={value} className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-sm ${checked ? (inverted ? 'border-white bg-white text-slate-950' : 'border-slate-900 bg-slate-900 text-white') : (inverted ? 'border-white/15 bg-white/5 text-slate-200' : 'border-slate-300 bg-white text-slate-700')}`}>
                  <input type="checkbox" checked={checked} onChange={() => togglePeripheral(value as KioskSessionPeripherals)} />
                  <span>{label}</span>
                </label>
              )
            })}
          </div>
          {peripherals.includes('other') ? (
            <UnderlineInput className={inverted ? 'text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]' : undefined} value={formData.peripheralsOther ?? ''} onChange={(event) => onChange({ peripheralsOther: event.target.value })} placeholder="Other peripheral" />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function FieldBlock(props: { label: string; children: ReactNode; inverted?: boolean }) {
  return (
    <div className="space-y-2">
      <FormFieldLabel className={props.inverted ? 'text-slate-200' : undefined}>{props.label}</FormFieldLabel>
      {props.children}
    </div>
  )
}

function TermsPanel(props: {
  terms?: Document
  isTablet: boolean
  signatureDataUrl: string | null
  onSignatureChange: (value: string | null) => void
  onSubmit?: () => void
  isSubmitting?: boolean
  errorMessage?: string | null
  inverted?: boolean
  submitLabel?: string
  extraFields?: ReactNode
  canSubmit?: boolean
  minimalChrome?: boolean
  /** Shown faintly in the watermark under the signature (with the date). */
  signatoryName?: string
  /**
   * When set, an editable Name field renders beside the pad, bound to
   * signatoryName. Leave unset if another field already edits the name
   * (check-out's "Collected by name") — the watermark still tracks the prop.
   */
  onSignatoryNameChange?: (value: string) => void
}) {
  const brand = useBranding()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawingRef = useRef(false)
  const watermarkRef = useRef<HTMLImageElement | null>(null)
  // Strokes are recorded so the watermark can repaint beneath them (e.g. the
  // name changes mid-signature) and the ink replays on top.
  const strokesRef = useRef<Array<Array<{ x: number; y: number }>>>([])
  const signatoryNameRef = useRef(props.signatoryName ?? '')

  // Faint brand logo + signatory name + signing date behind the signature.
  // Drawn onto the canvas itself (not CSS) so the exported PNG carries them.
  const drawSignatureBackground = useCallback(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return
    context.save()
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    const logo = watermarkRef.current
    if (logo?.complete && logo.naturalWidth > 0) {
      const width = canvas.width * 0.7
      const height = width * (logo.naturalHeight / logo.naturalWidth)
      context.globalAlpha = 0.08
      context.drawImage(
        logo,
        (canvas.width - width) / 2,
        (canvas.height - height) / 2 - 12,
        width,
        height,
      )
    }
    context.fillStyle = '#64748b'
    context.textAlign = 'center'
    const name = signatoryNameRef.current.trim()
    if (name) {
      context.globalAlpha = 0.5
      context.font = '26px system-ui, sans-serif'
      context.fillText(name, canvas.width / 2, canvas.height - 50)
    }
    context.globalAlpha = 0.45
    context.font = '22px system-ui, sans-serif'
    const dateLabel = new Date().toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    context.fillText(dateLabel, canvas.width / 2, canvas.height - 20)
    context.restore()
  }, [])

  const replayStrokes = useCallback(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return
    context.save()
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.lineWidth = 3
    context.strokeStyle = '#0f172a'
    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue
      context.beginPath()
      context.moveTo(stroke[0].x, stroke[0].y)
      for (let i = 1; i < stroke.length; i++) {
        context.lineTo(stroke[i].x, stroke[i].y)
      }
      context.stroke()
    }
    context.restore()
  }, [])

  // Repaint the watermark when the name changes — replaying any ink already
  // on the pad, and keeping the exported PNG in sync with what's visible.
  useEffect(() => {
    signatoryNameRef.current = props.signatoryName ?? ''
    if (!props.isTablet) return
    const canvas = canvasRef.current
    if (!canvas) return
    drawSignatureBackground()
    replayStrokes()
    if (strokesRef.current.length > 0) {
      props.onSignatureChange(canvas.toDataURL('image/png'))
    }
  }, [
    props.signatoryName,
    props.isTablet,
    props.onSignatureChange,
    drawSignatureBackground,
    replayStrokes,
  ])

  // Load the brand's light-background logo for the watermark. It is fetched
  // and drawn from a blob URL so the canvas stays untainted (toDataURL must
  // keep working even when PocketBase serves files from another origin).
  const watermarkUrl = brand.logoOnLightUrl
  useEffect(() => {
    if (!props.isTablet || !watermarkUrl) return
    const canvas = canvasRef.current
    if (!canvas) return
    let cancelled = false
    let logoObjectUrl: string | null = null
    fetch(watermarkUrl)
      .then((response) => (response.ok ? response.blob() : Promise.reject(response.status)))
      .then((blob) => {
        if (cancelled) return
        logoObjectUrl = URL.createObjectURL(blob)
        const logo = new Image()
        logo.onload = () => {
          if (cancelled) return
          watermarkRef.current = logo
          // Repaint with the logo once it arrives; any ink already on the pad
          // replays on top.
          drawSignatureBackground()
          replayStrokes()
          // If the customer signed before the logo finished loading, the held
          // data URL has no logo — re-export so the saved PNG matches the pad.
          if (strokesRef.current.length > 0) {
            props.onSignatureChange(canvas.toDataURL('image/png'))
          }
        }
        logo.src = logoObjectUrl
      })
      .catch(() => {
        // No logo — the plain white background and date stay as drawn.
      })
    return () => {
      cancelled = true
      if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl)
    }
  }, [props.isTablet, props.onSignatureChange, watermarkUrl, drawSignatureBackground, replayStrokes])

  useEffect(() => {
    if (!props.isTablet) return
    const canvas = canvasRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')
    if (!context) return

    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.lineWidth = 3
    context.strokeStyle = '#0f172a'
    drawSignatureBackground()

    const getPoint = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      return {
        x: ((event.clientX - rect.left) / rect.width) * canvas.width,
        y: ((event.clientY - rect.top) / rect.height) * canvas.height,
      }
    }

    const start = (event: PointerEvent) => {
      event.preventDefault()
      drawingRef.current = true
      canvas.setPointerCapture?.(event.pointerId)
      const point = getPoint(event)
      strokesRef.current.push([point])
      context.beginPath()
      context.moveTo(point.x, point.y)
    }

    const move = (event: PointerEvent) => {
      if (!drawingRef.current) return
      event.preventDefault()
      const point = getPoint(event)
      strokesRef.current[strokesRef.current.length - 1]?.push(point)
      context.lineTo(point.x, point.y)
      context.stroke()
      props.onSignatureChange(canvas.toDataURL('image/png'))
    }

    const end = (event?: PointerEvent) => {
      // Only export when a stroke was actually in progress — window-level
      // pointerups (taps on other fields/buttons) must not turn the blank
      // watermark canvas into a submittable "signature".
      if (!drawingRef.current) return
      drawingRef.current = false
      if (event) {
        canvas.releasePointerCapture?.(event.pointerId)
      }
      props.onSignatureChange(canvas.toDataURL('image/png'))
    }

    canvas.style.touchAction = 'none'
    canvas.addEventListener('pointerdown', start)
    canvas.addEventListener('pointermove', move)
    canvas.addEventListener('pointerup', end)
    canvas.addEventListener('pointercancel', end)
    window.addEventListener('pointerup', end)

    return () => {
      canvas.removeEventListener('pointerdown', start)
      canvas.removeEventListener('pointermove', move)
      canvas.removeEventListener('pointerup', end)
      canvas.removeEventListener('pointercancel', end)
      window.removeEventListener('pointerup', end)
    }
  }, [props.isTablet, props.onSignatureChange, drawSignatureBackground, replayStrokes])

  return (
    <div className={`grid h-full ${props.minimalChrome ? 'gap-5' : 'gap-6'} md:grid-cols-2`}>
      <div className={`min-h-0 overflow-hidden ${props.minimalChrome ? '' : `rounded-2xl border p-5 ${props.inverted ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-slate-50'}`}`}>
        <div className={`h-full overflow-auto text-sm leading-6 ${props.minimalChrome ? (props.inverted ? 'text-slate-100' : 'text-slate-700') : `rounded-xl p-4 ${props.inverted ? 'bg-slate-950/40 text-slate-100' : 'bg-white text-slate-700'}`}`}>
          <div className={props.inverted ? 'space-y-4 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-white [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-white [&_p]:text-slate-100 [&_strong]:text-white [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_li]:text-slate-100' : 'space-y-4 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-slate-900 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-900 [&_p]:text-slate-700 [&_strong]:text-slate-900 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_li]:text-slate-700'}>
            {renderSimpleMarkdown(props.terms?.body)}
          </div>
        </div>
      </div>
      <div className="flex min-h-0 flex-col gap-4">
        {props.isTablet ? (
          <>
            <div className={props.minimalChrome ? '' : `rounded-2xl border p-4 ${props.inverted ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-white'}`}>
              <div className="mb-3 flex items-end justify-between gap-4">
                {props.onSignatoryNameChange ? (
                  <div className="min-w-0 flex-1 max-w-sm">
                    <FieldBlock label="Name" inverted={props.inverted}>
                      <UnderlineInput
                        className={props.inverted ? 'text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]' : undefined}
                        value={props.signatoryName ?? ''}
                        onChange={(event) => props.onSignatoryNameChange?.(event.target.value)}
                        placeholder="Full name"
                      />
                    </FieldBlock>
                  </div>
                ) : (
                  <div />
                )}
                <button
                  type="button"
                  className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                  onClick={() => {
                    strokesRef.current = []
                    drawSignatureBackground()
                    props.onSignatureChange(null)
                  }}
                >
                  Clear
                </button>
              </div>
              <canvas ref={canvasRef} width={800} height={320} className="h-64 w-full touch-none rounded-xl bg-white" />
              <p className={props.inverted ? 'mt-2 text-xs text-slate-300' : 'mt-2 text-xs text-slate-500'}>Draw your signature, then confirm below.</p>
            </div>
            {props.extraFields ? <div className={props.minimalChrome ? '' : 'rounded-xl border border-white/10 bg-slate-950/30 p-4'}>{props.extraFields}</div> : null}
            {props.errorMessage ? <p className="text-sm text-rose-300">{props.errorMessage}</p> : null}
            <button
              type="button"
              disabled={!props.signatureDataUrl || props.isSubmitting || props.canSubmit === false}
              className="mt-auto rounded-2xl bg-emerald-500 px-6 py-4 text-lg font-semibold text-emerald-950 disabled:cursor-not-allowed disabled:bg-emerald-300"
              onClick={props.onSubmit}
            >
              {props.isSubmitting ? 'Submitting…' : (props.submitLabel ?? 'I agree & sign')}
            </button>
          </>
        ) : (
          <div className={`flex h-full min-h-[18rem] flex-col items-center justify-center rounded-2xl border border-dashed ${props.inverted ? 'border-white/20 text-slate-300' : 'border-slate-300 text-slate-500'} p-6 text-center`}>
            <p className="text-lg font-medium">Waiting for signature</p>
            <p className="mt-2 max-w-sm text-sm">The customer will review the terms and sign on the mounted tablet.</p>
          </div>
        )}
      </div>
    </div>
  )
}

function WizardNavigation(props: {
  currentStep: number
  canGoNext: boolean
  canGoBack: boolean
  onBack: () => void
  onNext: () => void
  inverted?: boolean
  isFinalStep?: boolean
  hideNext?: boolean
  showReset?: boolean
  centerContent?: ReactNode
}) {
  return (
    <div className="mt-6 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4">
      <button
        type="button"
        disabled={props.showReset ? false : !props.canGoBack}
        onClick={props.onBack}
        className={`justify-self-start rounded-2xl px-5 py-3 text-sm font-medium ${props.inverted ? 'border border-white/20 text-white disabled:text-white/40' : 'border border-slate-300 text-slate-700 disabled:text-slate-400'}`}
      >
        {props.showReset ? 'Reset' : 'Back'}
      </button>
      <div className="min-w-0 text-center">
        {props.centerContent}
      </div>
      {!props.hideNext ? (
        <button
          type="button"
          disabled={!props.canGoNext}
          onClick={props.onNext}
          className={`justify-self-end rounded-2xl px-5 py-3 text-sm font-medium ${props.inverted ? 'bg-white text-slate-950 disabled:bg-white/10 disabled:text-white/50' : 'bg-slate-900 text-white disabled:bg-slate-200 disabled:text-slate-400'}`}
        >
          {props.isFinalStep ? 'Ready to sign' : 'Next'}
        </button>
      ) : <div />}
    </div>
  )
}

function getKioskSessionForKiosk(kioskId: string, sessions: KioskSession[]) {
  return sessions.find((session) => session.kioskId === kioskId)
}

function getKioskDisplayName(kiosk?: Kiosk | null) {
  return kiosk?.friendlyName?.trim() || kiosk?.label || kiosk?.name || 'Kiosk'
}

type KioskControlTab = 'check-in-out' | 'devices'

function getKioskControlPath(tab: KioskControlTab = 'check-in-out') {
  return `/kiosk-control/${tab}`
}

function getPrimaryKioskForControl(kiosks: Kiosk[], sessions: KioskSession[]) {
  return kiosks.find((kiosk) => {
    const session = getKioskSessionForKiosk(kiosk.id, sessions)
    return session?.kioskType !== 'jobs-board'
  }) ?? kiosks[0]
}

function getStepTitle(step: number) {
  return KIOSK_STEP_TITLES[Math.max(0, step - 1)] ?? KIOSK_STEP_TITLES[0]
}

async function patchKioskSessionForm(
  db: ReturnType<typeof useDb>,
  sessionId: string,
  patch: Partial<KioskSessionFormData>,
) {
  await db.updateKioskSessionWithLatestAndWait(sessionId, (current) => ({
    formData: {
      ...(current.formData ?? {}),
      ...patch,
    },
  }))
}

async function setKioskSessionStep(
  db: ReturnType<typeof useDb>,
  sessionId: string,
  step: KioskStep,
) {
  await db.updateKioskSessionWithLatestAndWait(sessionId, () => ({
    currentStep: step,
  }))
}

// When advancing past the customer step with a customer already linked,
// persist any edits made to their details back onto the customer record.
async function syncLinkedCustomerOnStep(
  db: ReturnType<typeof useDb>,
  currentStep: number,
  nextStep: number,
  formData: KioskSessionFormData,
) {
  const leavingCustomerStep = currentStep === KIOSK_STEP_CUSTOMER && nextStep > KIOSK_STEP_CUSTOMER
  if (!leavingCustomerStep || !formData.customerId) return

  await db.upsertKioskCustomerAndWait(formData.customerId, buildCustomerPatch(formData))
}

async function startKioskCheckIn(
  db: ReturnType<typeof useDb>,
  sessionId: string,
  startedByUserId?: string,
) {
  await db.updateKioskSessionWithLatestAndWait(sessionId, (current) => ({
    flow: 'check-in',
    currentStep: KIOSK_STEP_CUSTOMER,
    formData: current.formData ?? {},
    jobId: undefined,
    lastCompletedFlow: undefined,
    startedBy: startedByUserId,
  }))
}

function buildCustomerPatch(formData: KioskSessionFormData) {
  return {
    fullName: formData.fullName?.trim() ?? '',
    contactPhone: normaliseOptional(formData.contactPhone),
    contactEmail: normaliseOptional(formData.contactEmail),
    contactAddress: normaliseOptional(formData.contactAddress),
    invoiceName: normaliseOptional(formData.invoiceName) ?? formData.fullName?.trim() ?? '',
    invoiceAddress: normaliseOptional(formData.invoiceAddress) ?? normaliseOptional(formData.contactAddress) ?? '',
    invoiceVat: normaliseOptional(formData.invoiceVat),
    invoiceEmail: normaliseOptional(formData.invoiceEmail) ?? normaliseOptional(formData.contactEmail) ?? '',
    invoicePhone: normaliseOptional(formData.invoicePhone) ?? normaliseOptional(formData.contactPhone) ?? '',
  }
}

type KioskSignatureSubmission = {
  signatureDataUrl?: string | null
  useNoSignatureReason?: boolean
  noSignatureReason?: string
  formData: KioskSessionFormData
}

function KioskWizardShared(props: {
  kiosk: Kiosk
  session: KioskSession
  terms?: Document
  onPatch: (patch: Partial<KioskSessionFormData>) => Promise<void>
  onStepChange: (step: number) => Promise<void>
  onSubmit?: (submission: KioskSignatureSubmission) => Promise<void>
  onReset?: () => Promise<void>
  isTablet?: boolean
  sessionBadge?: string
}) {
  const { session, terms, onPatch, onStepChange, onSubmit } = props
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)
  const currentStep = session.currentStep as KioskStep
  const [localFormData, setLocalFormData] = useState<KioskSessionFormData>(session.formData ?? {})
  const lastSessionIdentityRef = useRef(`${session.id}:${currentStep}:${session.flow}`)
  const localFormDataRef = useRef(localFormData)
  const formData = localFormData
  const canGoNext = getStepValidity(currentStep, formData)
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const incoming = session.formData ?? {}
    const nextIdentity = `${session.id}:${currentStep}:${session.flow}`

    // New session / step / flow: fully reset local form state.
    if (lastSessionIdentityRef.current !== nextIdentity) {
      lastSessionIdentityRef.current = nextIdentity
      localFormDataRef.current = incoming
      setLocalFormData(incoming)
      return
    }

    // Echo of our own write (possibly a stale one that lands after newer
    // keystrokes): never overwrite local state — what we hold is the newest.
    if (session.lastUpdateOrigin && session.lastUpdateOrigin === KIOSK_CLIENT_ORIGIN) {
      return
    }

    // Genuine remote update from another listening session: sync it in.
    if (JSON.stringify(incoming) !== JSON.stringify(localFormDataRef.current)) {
      localFormDataRef.current = incoming
      setLocalFormData(incoming)
    }
  }, [session.formData, session.lastUpdateOrigin, session.id, currentStep, session.flow])

  useEffect(() => {
    if (currentStep !== KIOSK_STEP_TERMS) {
      setSignatureDataUrl(null)
      setSubmitError(null)
      setIsSubmitting(false)
    }
  }, [currentStep])

  const handlePatch = async (patch: Partial<KioskSessionFormData>) => {
    setLocalFormData((current) => {
      const next = { ...current, ...patch }
      // Keep the ref in lockstep so the reconciliation effect treats this
      // local edit (and its realtime echo) as ours, not a remote change.
      localFormDataRef.current = next
      return next
    })
    await onPatch(patch)
  }

  const handleSubmit = async (submission: KioskSignatureSubmission) => {
    if (!onSubmit) return
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await onSubmit(submission)
    } catch (error) {
      setSubmitError(
        formatPocketBaseError(error, {
          fallbackMessage: 'Unable to complete check-in right now. Please try again.',
          fieldLabels: {
            kiosk_id: 'Kiosk',
            current_step: 'Step',
            form_data: 'Form data',
            job_id: 'Job',
          },
        }),
      )
      setIsSubmitting(false)
    }
  }

  const isCheckout = session.flow === 'check-out'

  const body =
    isCheckout ? (
      <div className="space-y-6">
        <TermsPanel
          terms={terms}
          isTablet
          signatureDataUrl={signatureDataUrl}
          onSignatureChange={setSignatureDataUrl}
          onSubmit={() => void handleSubmit({ signatureDataUrl, formData })}
          isSubmitting={isSubmitting}
          errorMessage={submitError}
          inverted
          minimalChrome
          submitLabel="Agree and sign"
          canSubmit={Boolean(formData.checkedOutName?.trim())}
          signatoryName={formData.checkedOutName ?? ''}
          extraFields={(
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldBlock label="Collected by name" inverted>
                <UnderlineInput
                  className="text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]"
                  value={formData.checkedOutName ?? ''}
                  onChange={(event) => { void handlePatch({ checkedOutName: event.target.value }) }}
                  placeholder="Full name"
                />
              </FieldBlock>
              <FieldBlock label="Collected by cell" inverted>
                <UnderlineInput
                  className="text-white placeholder:text-slate-400 shadow-[0_1px_0_0_rgba(148,163,184,0.6)] focus:shadow-[0_1px_0_0_rgba(255,255,255,0.95)]"
                  value={formData.checkedOutPhone ?? ''}
                  onChange={(event) => { void handlePatch({ checkedOutPhone: event.target.value }) }}
                  placeholder="Optional"
                />
              </FieldBlock>
            </div>
          )}
        />
      </div>
    ) : currentStep === KIOSK_STEP_CUSTOMER ? (
      <CustomerFields formData={formData} onChange={handlePatch} inverted />
    ) : currentStep === KIOSK_STEP_DEVICE ? (
      <DeviceFields formData={formData} onChange={handlePatch} inverted />
    ) : (
      <TermsPanel
        terms={terms}
        isTablet
        signatureDataUrl={signatureDataUrl}
        onSignatureChange={setSignatureDataUrl}
        onSubmit={() => void handleSubmit({ signatureDataUrl, formData })}
        isSubmitting={isSubmitting}
        errorMessage={submitError}
        inverted
        signatoryName={formData.fullName ?? ''}
        onSignatoryNameChange={(value) => {
          void handlePatch({ fullName: value })
        }}
      />
    )

  return (
    <div className="relative flex h-full min-h-full w-full flex-col gap-4 bg-slate-950 px-5 py-4 text-white">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">{isCheckout ? 'Check out' : getStepTitle(currentStep)}</h1>
          {isCheckout ? (
            <div className="mt-4 space-y-2 text-base leading-7 text-slate-200 md:text-lg">
              {(formData.deviceType || formData.deviceDescription) ? (
                <p>
                  <span className="font-medium text-white">Device:</span> {[formData.deviceType, formData.deviceDescription].filter(Boolean).join(' · ')}
                </p>
              ) : null}
              {formData.fault ? (
                <p>
                  <span className="font-medium text-white">Fault:</span> {formData.fault}
                </p>
              ) : null}
              {formData.peripherals?.length ? (
                <p>
                  <span className="font-medium text-white">Peripherals:</span> {[
                    ...(formData.peripherals ?? []).filter((entry) => entry !== 'other'),
                    ...(formData.peripherals?.includes('other') && formData.peripheralsOther?.trim()
                      ? [formData.peripheralsOther.trim()]
                      : []),
                  ].join(' · ')}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {!isCheckout ? <StepProgress currentStep={Math.max(0, currentStep - 1)} inverted /> : null}

      <div className="flex min-h-0 flex-1 flex-col">
        {body}
      </div>

      {!isCheckout ? (
        <WizardNavigation
          currentStep={currentStep}
          canGoBack={currentStep > KIOSK_STEP_CUSTOMER}
          canGoNext={currentStep < KIOSK_STEP_TERMS ? canGoNext : false}
          onBack={() => {
            if (!props.isTablet && currentStep === KIOSK_STEP_CUSTOMER && props.onReset) {
              setConfirmResetOpen(true)
              return
            }
            void onStepChange(Math.max(KIOSK_STEP_CUSTOMER, currentStep - 1) as KioskStep)
          }}
          onNext={() => onStepChange(Math.min(KIOSK_STEP_TERMS, currentStep + 1) as KioskStep)}
          hideNext={currentStep === KIOSK_STEP_TERMS}
          isFinalStep={currentStep === KIOSK_STEP_DEVICE}
          showReset={!props.isTablet && currentStep === KIOSK_STEP_CUSTOMER}
          centerContent={
            (props.sessionBadge ?? session.pairingCode) ? (
              <div className="font-mono text-xs font-medium tracking-[0.2em] text-white/70">
                {props.sessionBadge ?? session.pairingCode}
              </div>
            ) : null
          }
          inverted
        />
      ) : null}

      <Dialog open={confirmResetOpen} onOpenChange={setConfirmResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset kiosk session?</DialogTitle>
            <DialogDescription>
              This will clear the current check-in and send the kiosk back to idle.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setConfirmResetOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              onClick={() => {
                setConfirmResetOpen(false)
                void props.onReset?.()
              }}
            >
              Reset
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function KioskWizardBody(props: {
  kiosk: Kiosk
  session: KioskSession
  terms?: Document
  isTablet: boolean
  onPatch: (patch: Partial<KioskSessionFormData>) => Promise<void>
  onStepChange: (step: number) => Promise<void>
  onSubmit?: (submission: KioskSignatureSubmission) => Promise<void>
  onReset?: () => Promise<void>
  sessionBadge?: string
}) {
  const shell = (
    <KioskWizardShared
      kiosk={props.kiosk}
      session={props.session}
      terms={props.terms}
      onPatch={props.onPatch}
      onStepChange={props.onStepChange}
      onSubmit={props.onSubmit}
      onReset={props.onReset}
      isTablet={props.isTablet}
      sessionBadge={props.sessionBadge}
    />
  )

  if (props.isTablet) {
    return (
      <div className="absolute inset-0 overflow-hidden bg-slate-950 text-white">
        <div className="flex h-full flex-col overflow-auto">
          {shell}
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-[2rem] ring-1 ring-white/10">
      {shell}
    </div>
  )
}

type KioskOverviewMode = 'idle' | 'checkout-selector'

function buildCheckoutFormData(job: Job, customer?: Customer): KioskSessionFormData {
  return {
    customerId: customer?.id ?? job.customerId,
    fullName: customer?.fullName,
    contactPhone: customer?.contactPhone,
    contactEmail: customer?.contactEmail,
    contactAddress: customer?.contactAddress,
    invoiceName: customer?.invoiceName,
    invoiceAddress: customer?.invoiceAddress,
    invoiceVat: customer?.invoiceVat,
    invoiceEmail: customer?.invoiceEmail,
    invoicePhone: customer?.invoicePhone,
    deviceType: job.deviceType,
    deviceDescription: job.deviceDescription,
    devicePassword: job.devicePassword,
    fault: job.fault,
  }
}

function KioskCheckoutSelectorPanel(props: {
  onBack: () => void
  onStartCheckout: (jobId: string) => Promise<void>
}) {
  const [startingJobId, setStartingJobId] = useState<string | null>(null)
  const { isLoading, isError, services, users } = useServiceItems()
  const mutations = useServiceMutations()

  if (isLoading || isError) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
        {isLoading ? 'Loading jobs…' : 'Unable to load jobs right now.'}
      </div>
    )
  }

  const readyJobs = services.filter(
    (item) => item.type === 'job' && isReadyForCollection(item.status as Job['status']),
  )

  const checkoutColumn: LedgerColumn<ServiceItem> = {
    key: 'checkout',
    label: '',
    widthClass: 'w-36',
    cellClass: 'whitespace-nowrap text-right',
    render: (item) => (
      <button
        type="button"
        disabled={startingJobId === item.privateId}
        onClick={(event) => {
          event.stopPropagation()
          setStartingJobId(item.privateId)
          void props.onStartCheckout(item.privateId).finally(() =>
            setStartingJobId((current) => (current === item.privateId ? null : current)),
          )
        }}
        className="cursor-pointer rounded-2xl bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-default disabled:bg-slate-300"
      >
        {startingJobId === item.privateId ? 'Starting…' : 'Start check-out'}
      </button>
    ),
  }

  return (
    <ServicesScreen
      embedded
      services={readyJobs}
      users={users}
      {...mutations}
      title="Ready for collection"
      description="Fetch the device from the shelf by its tag, then start check-out. Click a row for full details."
      emptyLabel="No jobs are currently in Ready for Collection."
      extraColumns={[checkoutColumn]}
      hideColumns={['type']}
      autoSelectFirst={false}
      actions={
        <button
          type="button"
          className="h-9 cursor-pointer rounded-lg border border-slate-300 bg-white px-3.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
          onClick={props.onBack}
        >
          Back
        </button>
      }
    />
  )
}

function KioskPairDevicePanel(props: {
  kiosk: Kiosk
  session: KioskSession
  devices: Array<{ id: string; deviceUuid: string; pairingCode?: string; friendlyName?: string; userAgent?: string; status: 'active' | 'revoked'; pairedAt?: string; lastSeenAt?: string; revokedAt?: string }>
  isRevokingDeviceId: string | null
  onRefreshDevices: () => Promise<void>
  onRevokeDevice: (deviceId: string) => Promise<void>
}) {
  const db = useDb() as ReturnType<typeof useDb> & { pb?: { send: Function } }
  const [code, setCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)
  const [confirmRevokeDevice, setConfirmRevokeDevice] = useState<null | { id: string; deviceUuid: string; userAgent?: string }>(null)

  const tabletHomeUrl = typeof window === 'undefined'
    ? '/kiosk'
    : new URL('/kiosk', window.location.origin).toString()

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    if (!db.pb) return

    setIsSubmitting(true)
    setError(null)
    setMessage(null)
    try {
      await db.pb.send('/api/kiosk/pairing/approve', {
        method: 'POST',
        body: {
          kioskId: props.kiosk.id,
          code: code.trim().toUpperCase(),
        },
      })
      await props.onRefreshDevices()
      setMessage(`Tablet paired for ${getKioskDisplayName(props.kiosk)}.`)
      setCode('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to pair kiosk tablet.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRevokeDevice = async (deviceId: string) => {
    setRevokeError(null)
    try {
      await props.onRevokeDevice(deviceId)
      setConfirmRevokeDevice(null)
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Unable to revoke device.')
    }
  }

  return (
    <PanelShell title="Paired devices" subtitle="Pair a new tablet for this kiosk and manage existing authenticated browsers." light>
      <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 text-slate-900 shadow-sm">
        <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Approve pairing</h3>
              <p className="text-xs leading-5 text-slate-500">
                Open <span className="font-mono text-blue-700">{tabletHomeUrl}</span> on the tablet, wait for the 6-character code, then enter it here.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <FormFieldLabel>6-character code</FormFieldLabel>
            <UnderlineInput
              name="code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              className="w-full text-center text-3xl tracking-[0.35em] text-slate-900"
              placeholder="ABC123"
              autoCapitalize="characters"
              autoCorrect="off"
              maxLength={6}
            />
          </div>

          <p className="text-sm text-slate-500">Target kiosk: {getKioskDisplayName(props.kiosk)}</p>
          {message ? <p className="text-sm text-emerald-600">{message}</p> : null}
          {error ? <p className="text-sm text-rose-600">{error}</p> : null}

          <button
            type="submit"
            disabled={isSubmitting || code.trim().length !== 6}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? 'Pairing…' : 'Pair tablet'}
          </button>
        </form>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Paired devices</h3>
              <p className="text-xs text-slate-500">List and revoke authenticated browsers for this kiosk.</p>
            </div>
          </div>
          {revokeError ? <p className="mt-4 text-sm text-rose-600">{revokeError}</p> : null}
          <div className="mt-4 space-y-3">
            {props.devices.length === 0 ? (
              <p className="text-sm text-slate-500">No paired devices yet.</p>
            ) : props.devices.map((device) => (
              <div key={device.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{getKioskDisplayName(props.kiosk)}</span>
                    {device.pairingCode ? (
                      <span className="rounded-full bg-cyan-50 px-2 py-0.5 font-mono text-[11px] font-semibold tracking-[0.2em] text-cyan-700">
                        {device.pairingCode}
                      </span>
                    ) : null}
                    <span className={device.status === 'active' ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700' : 'rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700'}>
                      {device.status}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{device.userAgent || 'Unknown browser'}</p>
                  <p className="mt-1 break-all font-mono text-xs text-slate-500">{device.deviceUuid}</p>
                  <p className="mt-1 text-xs text-slate-500">Last seen: {device.lastSeenAt || 'Never'}{device.revokedAt ? ` • Revoked: ${device.revokedAt}` : ''}</p>
                </div>
                {device.status === 'active' ? (
                  <button
                    type="button"
                    disabled={props.isRevokingDeviceId === device.id}
                    className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-70"
                    onClick={() => setConfirmRevokeDevice({
                      id: device.id,
                      deviceUuid: device.deviceUuid,
                      userAgent: device.userAgent,
                    })}
                  >
                    {props.isRevokingDeviceId === device.id ? 'Revoking…' : 'Revoke'}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <Dialog open={confirmRevokeDevice !== null} onOpenChange={(open) => {
          if (!open && props.isRevokingDeviceId === null) {
            setConfirmRevokeDevice(null)
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Revoke paired device?</DialogTitle>
              <DialogDescription>
                This will immediately disconnect the paired tablet/browser and remove it from the active device list.
              </DialogDescription>
            </DialogHeader>
            {confirmRevokeDevice ? (
              <div className="space-y-2 text-sm text-slate-600">
                {confirmRevokeDevice.userAgent ? <p>{confirmRevokeDevice.userAgent}</p> : null}
                <p className="break-all font-mono text-xs text-slate-500">{confirmRevokeDevice.deviceUuid}</p>
              </div>
            ) : null}
            <DialogFooter>
              <button
                type="button"
                disabled={props.isRevokingDeviceId !== null}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-70"
                onClick={() => setConfirmRevokeDevice(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!confirmRevokeDevice || props.isRevokingDeviceId !== null}
                className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-70"
                onClick={() => {
                  if (!confirmRevokeDevice) return
                  void handleRevokeDevice(confirmRevokeDevice.id)
                }}
              >
                {props.isRevokingDeviceId === confirmRevokeDevice?.id ? 'Revoking…' : 'Revoke device'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PanelShell>
  )
}

function KioskCheckoutFinishPanel(props: {
  kiosk: Kiosk
  job?: Job
  onReset: () => Promise<void>
}) {
  return (
    <div className="mx-auto w-full max-w-3xl">
      <PanelShell title={getKioskDisplayName(props.kiosk)} subtitle="The customer has completed check-out on the tablet." light>
        <div className="flex h-full min-h-[24rem] flex-col items-center justify-center rounded-3xl border border-emerald-200 bg-linear-to-br from-emerald-50 to-white p-10 text-center">
          <div className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-900">
            Check-out captured
          </div>
          <h2 className="mt-5 text-3xl font-semibold text-slate-900">Finish up</h2>
          <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
            {props.job?.checkedOutSignature
              ? 'Collection details and signature are recorded. This job is now ready to invoice.'
              : 'Collection details are recorded. This job is now ready to invoice.'}
          </p>
          {props.job ? (
            <div className="mt-6 space-y-2 text-sm text-slate-700">
              <div>
                <span className="font-medium">Job:</span>{' '}
                <Link
                  to="/jobs/$id"
                  params={{ id: props.job.publicId ?? props.job.id }}
                  className="font-semibold text-blue-700 hover:underline"
                >
                  {props.job.publicId ?? props.job.id}
                </Link>
              </div>
              {props.job.checkedOutName ? <div><span className="font-medium">Collected by:</span> {props.job.checkedOutName}</div> : null}
              {props.job.checkedOutPhone ? <div><span className="font-medium">Phone:</span> {props.job.checkedOutPhone}</div> : null}
              {props.job.checkedOutNoSignatureReason ? <div><span className="font-medium">No sig reason:</span> {props.job.checkedOutNoSignatureReason}</div> : null}
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                void props.onReset()
              }}
              className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:bg-slate-300"
            >
              Back to kiosk home
            </button>
          </div>
        </div>
      </PanelShell>
    </div>
  )
}

function KioskJobsBoardPanel(props: {
  kiosk: Kiosk
  jobs: Job[]
  assignedUserNameByJobId: Map<string, string>
  compact?: boolean
}) {
  const columns = JOB_BOARD_COLUMNS

  return (
    <PanelShell
      title={getKioskDisplayName(props.kiosk)}
      subtitle="Static technician jobs board driven by live job statuses."
      light={!props.compact}
    >
      <div className={`grid gap-4 ${props.compact ? 'xl:grid-cols-3' : 'lg:grid-cols-3'}`}>
        {columns.map((column) => {
          const items = props.jobs
            .filter((job) => column.statuses.includes(job.status))
            .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))

          return (
            <div
              key={column.title}
              className={props.compact
                ? 'rounded-2xl border border-white/10 bg-white/5 p-4 text-white'
                : 'rounded-2xl border border-slate-200 bg-white p-4 text-slate-900 shadow-sm'}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">{column.title}</h3>
                <span className={props.compact ? 'text-xs text-slate-300' : 'text-xs text-slate-500'}>{items.length}</span>
              </div>
              <div className="mt-4 space-y-3">
                {items.length === 0 ? (
                  <p className={props.compact ? 'text-sm text-slate-300' : 'text-sm text-slate-500'}>No jobs.</p>
                ) : items.map((job) => (
                  <div
                    key={job.id}
                    className={props.compact
                      ? 'rounded-xl border border-white/10 bg-black/20 px-4 py-3'
                      : 'rounded-xl border border-slate-200 bg-slate-50 px-4 py-3'}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold">{job.publicId ?? job.id}</span>
                      <span className={props.compact ? 'text-[11px] text-slate-300' : 'text-[11px] text-slate-500'}>{job.status}</span>
                    </div>
                    <p className={`mt-2 text-sm ${props.compact ? 'text-slate-100' : 'text-slate-700'}`}>{[job.deviceType, job.deviceDescription].filter(Boolean).join(' · ')}</p>
                    <p className={`mt-1 text-xs ${props.compact ? 'text-slate-300' : 'text-slate-500'}`}>{job.fault}</p>
                    <p className={`mt-2 text-xs ${props.compact ? 'text-cyan-200' : 'text-slate-500'}`}>
                      {props.assignedUserNameByJobId.get(job.id) ?? 'Unassigned'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </PanelShell>
  )
}

export function KioskScreenContainer() {
  const router = useRouter()
  const location = useLocation()
  const db = useDb() as ReturnType<typeof useDb> & { pb?: { send: Function } }
  const pb = usePocketBase()
  const { user: authUser } = useAuth()
  const { kiosks, sessions, documents, pairedDevices } = useKioskData()
  const customersResult = useCustomersSnapshot()
  const jobsResult = useLiveQuery((q) => q.from({ job: db.jobsCollection }))
  const assignmentsResult = useLiveQuery((q) => q.from({ assignments: db.jobAssignmentsCollection }))
  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const [overviewMode, setOverviewMode] = useState<KioskOverviewMode>('idle')
  const [confirmResetOpen, setConfirmResetOpen] = useState(false)
  const [confirmNoSignatureOpen, setConfirmNoSignatureOpen] = useState(false)
  const [noSignatureReason, setNoSignatureReason] = useState('')
  const [noSignatureCheckedOutName, setNoSignatureCheckedOutName] = useState('')
  const [isSubmittingNoSignature, setIsSubmittingNoSignature] = useState(false)
  const [noSignatureSubmitError, setNoSignatureSubmitError] = useState<string | null>(null)
  const [tagConflict, setTagConflict] = useState<JobNumberTagConflict | null>(null)
  const [isRevokingDeviceId, setIsRevokingDeviceId] = useState<string | null>(null)
  const [optimisticallyRevokedDeviceIds, setOptimisticallyRevokedDeviceIds] = useState<string[]>([])

  const kioskRows = (kiosks.data ?? []) as Kiosk[]
  const sessionRows = (sessions.data ?? []) as KioskSession[]
  const [customerOption, setCustomerOption] = useState<SearchOption | null>(null)
  const patchTimeoutRef = useRef<number | null>(null)
  const pendingPatchRef = useRef<Partial<KioskSessionFormData>>({})

  useEffect(() => {
    return () => {
      if (patchTimeoutRef.current !== null) {
        window.clearTimeout(patchTimeoutRef.current)
      }
    }
  }, [])

  const selectedKiosk = useMemo(
    () => getPrimaryKioskForControl(kioskRows, sessionRows),
    [kioskRows, sessionRows],
  )
  const activeTab: KioskControlTab = location.pathname === '/kiosk-control/devices' ? 'devices' : 'check-in-out'

  useEffect(() => {
    if (!selectedKiosk) return
    if (!location.pathname.startsWith('/kiosk-control')) return
    if (location.pathname === '/kiosk-control/check-in-out' || location.pathname === '/kiosk-control/devices') return
    router.navigate({ to: getKioskControlPath('check-in-out'), replace: true })
  }, [router, location.pathname, selectedKiosk])

  if (kiosks.isLoading || sessions.isLoading || documents.isLoading || customersResult.isLoading || jobsResult.isLoading || assignmentsResult.isLoading || usersResult.isLoading) {
    return <KioskLoadingState />
  }

  if (kiosks.isError || sessions.isError || documents.isError || customersResult.isError || jobsResult.isError || assignmentsResult.isError || usersResult.isError) {
    return <KioskErrorState message="Unable to load kiosk control panel right now." />
  }

  const usersById = new Map(((usersResult.data ?? []) as { id: string; name: string }[]).map((user) => [user.id, user.name]))
  const assignedUserNameByJobId = new Map<string, string>()
  for (const assignment of (assignmentsResult.data ?? []) as { jobId: string; userId: string; unassignedAt?: string; assignedAt?: string; createdAt: string }[]) {
    if (assignment.unassignedAt) continue
    if (assignedUserNameByJobId.has(assignment.jobId)) continue
    assignedUserNameByJobId.set(assignment.jobId, usersById.get(assignment.userId) ?? 'Assigned')
  }
  if (!selectedKiosk) {
    return <KioskEmptyState message="No kiosks have been registered yet." />
  }

  const session = getKioskSessionForKiosk(selectedKiosk.id, sessionRows)
  if (!session) {
    return <KioskErrorState message="This kiosk is missing its session record. Seed data still needs to be created." />
  }

  const flushPendingPatch = async () => {
    if (patchTimeoutRef.current !== null) {
      window.clearTimeout(patchTimeoutRef.current)
      patchTimeoutRef.current = null
    }

    const pendingPatch = pendingPatchRef.current
    if (Object.keys(pendingPatch).length === 0) return

    pendingPatchRef.current = {}
    await patchKioskSessionForm(db, session.id, pendingPatch)
  }

  const patchSession = async (patch: Partial<KioskSessionFormData>) => {
    pendingPatchRef.current = {
      ...pendingPatchRef.current,
      ...patch,
    }

    if (patchTimeoutRef.current !== null) {
      window.clearTimeout(patchTimeoutRef.current)
    }

    patchTimeoutRef.current = window.setTimeout(() => {
      void flushPendingPatch()
    }, KIOSK_FORM_SYNC_DEBOUNCE_MS)
  }

  const linkCustomer = (option: SearchOption | null) => {
    setCustomerOption(option)
    if (option) {
      const c = option as SearchOption & {
        fullName?: string
        contactPhone?: string
        contactEmail?: string
        contactAddress?: string
        invoiceName?: string
        invoiceAddress?: string
        invoiceEmail?: string
        invoicePhone?: string
        invoiceVat?: string
      }
      void patchSession({
        customerId: String(c.id ?? ''),
        fullName: c.fullName,
        contactPhone: c.contactPhone,
        contactEmail: c.contactEmail,
        contactAddress: c.contactAddress,
        invoiceName: c.invoiceName,
        invoiceAddress: c.invoiceAddress,
        invoiceEmail: c.invoiceEmail,
        invoicePhone: c.invoicePhone,
        invoiceVat: c.invoiceVat,
      })
    } else {
      void patchSession({ customerId: undefined })
    }
  }

  const startCheckIn = async () => {
    setOverviewMode('idle')
    await flushPendingPatch()
    await startKioskCheckIn(db, session.id, authUser?.id)
  }

  const startCheckout = async (jobId: string) => {
    const job = jobs.find((entry) => entry.id === jobId)
    if (!job) throw new Error('Job not found')
    const customer = customersById.get(job.customerId)
    await flushPendingPatch()
    await db.updateKioskSessionWithLatestAndWait(session.id, () => ({
      flow: 'check-out',
      currentStep: KIOSK_STEP_TERMS,
      formData: buildCheckoutFormData(job, customer),
      jobId: job.id,
      lastCompletedFlow: undefined,
      startedBy: authUser?.id,
    }))
    setOverviewMode('idle')
  }

  const reset = async () => {
    setCustomerOption(null)
    setOverviewMode('idle')
    await flushPendingPatch()
    await db.resetKioskSessionAndWait(session.id)
  }

  const submitCheckout = async (submission: KioskSignatureSubmission) => {
    await flushPendingPatch()
    if (!pb) {
      throw new Error('PocketBase client unavailable for kiosk submission')
    }
    if (!session.jobId) {
      throw new Error('No job selected for kiosk check-out')
    }
    if (!submission.formData.checkedOutName?.trim()) {
      throw new Error('Collected by name is required')
    }

    const noSignatureReason = submission.noSignatureReason?.trim() ?? ''
    const useNoSignatureReason = Boolean(submission.useNoSignatureReason)
    if (useNoSignatureReason && !noSignatureReason) {
      throw new Error('Reason no signature was captured is required')
    }
    if (!useNoSignatureReason && !submission.signatureDataUrl) {
      throw new Error('Signature is required')
    }

    await db.completeKioskCheckoutAndWait({
      jobId: String(session.jobId),
      checkedOutName: submission.formData.checkedOutName,
      checkedOutPhone: submission.formData.checkedOutPhone,
      processedBy: session.startedBy,
      noSignatureReason: useNoSignatureReason ? noSignatureReason : undefined,
      signatureFile: useNoSignatureReason
        ? undefined
        : dataUrlToFile(submission.signatureDataUrl!, `checkout-signature-${Date.now()}.png`),
    })

    await db.updateKioskSessionWithLatestAndWait(session.id, () => ({
      flow: 'idle',
      currentStep: KIOSK_STEP_CUSTOMER,
      formData: {},
      jobId: session.jobId,
      lastCompletedFlow: 'check-out',
      startedBy: undefined,
    }))
  }

  const submitCheckInWithoutSignature = async (submission: KioskSignatureSubmission) => {
    await flushPendingPatch()
    if (!pb) {
      throw new Error('PocketBase client unavailable for kiosk submission')
    }

    const customerFields = buildCustomerPatch(submission.formData)
    let customerId = submission.formData.customerId

    if (customerId) {
      await db.upsertKioskCustomerAndWait(customerId, customerFields)
    } else {
      const customer = await db.createCustomerAndWait({
        fullName: customerFields.fullName,
        contactPhone: customerFields.contactPhone,
        contactEmail: customerFields.contactEmail,
        contactAddress: customerFields.contactAddress,
        invoiceName: customerFields.invoiceName,
        invoiceAddress: customerFields.invoiceAddress,
        invoiceVat: customerFields.invoiceVat,
        invoiceEmail: customerFields.invoiceEmail,
        invoicePhone: customerFields.invoicePhone,
      })
      customerId = customer.id
    }

    const noSignatureReason = submission.noSignatureReason?.trim() ?? ''
    const useNoSignatureReason = Boolean(submission.useNoSignatureReason)
    if (useNoSignatureReason && !noSignatureReason) {
      throw new Error('Reason no signature was captured is required')
    }
    if (!useNoSignatureReason && !submission.signatureDataUrl) {
      throw new Error('Signature is required')
    }

    const jobRecord = await db.createKioskJobAndWait({
      customerId,
      formData: submission.formData,
      processedBy: session.startedBy,
      noSignatureReason: useNoSignatureReason ? noSignatureReason : undefined,
      signatureFile: useNoSignatureReason
        ? undefined
        : dataUrlToFile(submission.signatureDataUrl!, `checkin-signature-${Date.now()}.png`),
    })

    await db.updateKioskSessionWithLatestAndWait(session.id, () => ({
      flow: 'idle',
      currentStep: KIOSK_STEP_CUSTOMER,
      formData: {},
      jobId: String(jobRecord.id),
      lastCompletedFlow: 'check-in',
      startedBy: undefined,
    }))
  }

  const handleCompleteWithoutSignature = async () => {
    if (isSubmittingNoSignature) return
    setNoSignatureSubmitError(null)
    setIsSubmittingNoSignature(true)
    try {
      if (session.flow === 'check-out') {
        await submitCheckout({
          useNoSignatureReason: true,
          noSignatureReason,
          formData: {
            ...session.formData,
            ...pendingPatchRef.current,
            checkedOutName: noSignatureCheckedOutName.trim() || session.formData.checkedOutName,
          },
        })
      } else {
        await submitCheckInWithoutSignature({
          useNoSignatureReason: true,
          noSignatureReason,
          formData: {
            ...session.formData,
            ...pendingPatchRef.current,
          },
        })
      }
      setConfirmNoSignatureOpen(false)
      setNoSignatureReason('')
    } catch (error) {
      setNoSignatureSubmitError(
        formatPocketBaseError(error, {
          fallbackMessage: 'Unable to complete check-out right now. Please try again.',
          fieldLabels: {
            kiosk_id: 'Kiosk',
            current_step: 'Step',
            form_data: 'Form data',
            job_id: 'Job',
          },
        }),
      )
    } finally {
      setIsSubmittingNoSignature(false)
    }
  }

  const assignTagAndStart = async (tag: string) => {
    if (!session.jobId) return
    if (!pb) {
      throw new Error('PocketBase client unavailable for tag assignment')
    }

    const normalizedTag = normalizeJobNumberTag(tag)
    const conflict = findJobNumberTagConflict(jobs, session.jobId, normalizedTag)
    if (conflict) {
      setTagConflict(conflict)
      throw new Error(conflict.message)
    }

    try {
      await db.assignJobNumberTagAndStartAndWait(String(session.jobId), normalizedTag)
    } catch (error) {
      if (isPocketBaseJobNumberTagUniqueError(error)) {
        const freshConflict = findJobNumberTagConflict(jobs, session.jobId, normalizedTag)
        const resolvedConflict = freshConflict ?? {
          tag: normalizedTag,
          message: `Number tag ${normalizedTag} is already assigned to another job.`,
        }
        setTagConflict(resolvedConflict)
        throw new Error(resolvedConflict.message)
      }
      throw error
    }
  }

  const jobs = (jobsResult.data ?? []) as Job[]
  const customers = (customersResult.data ?? []) as Customer[]
  const customersById = new Map(customers.map((customer) => [customer.id, customer]))
  const sessionJob = session.jobId ? jobs.find((job) => job.id === session.jobId) : undefined
  const isIdle = session.flow === 'idle'
  const hasCompletedCheckIn = isIdle && session.lastCompletedFlow === 'check-in' && Boolean(session.jobId)
  const hasCompletedCheckout = isIdle && session.lastCompletedFlow === 'check-out' && Boolean(session.jobId)
  const terms =((documents.data ?? []) as Document[]).find((document) => document.key === CHECKIN_TERMS_KEY)
  const checkoutTerms = ((documents.data ?? []) as Document[]).find((document) => document.key === CHECKOUT_TERMS_KEY)
  const pairedDeviceRows = ((pairedDevices?.data ?? []) as Array<{ id: string; kioskId: string; deviceUuid: string; pairingCode?: string; friendlyName?: string; userAgent?: string; status: 'active' | 'revoked'; pairedAt?: string; lastSeenAt?: string; revokedAt?: string }>)
    .filter((device) => device.kioskId === selectedKiosk.id)
    .filter((device) => device.status === 'active')
    .filter((device) => !optimisticallyRevokedDeviceIds.includes(device.id))
    .sort((a, b) => String(b.lastSeenAt ?? b.pairedAt ?? '').localeCompare(String(a.lastSeenAt ?? a.pairedAt ?? '')))

  const revokeDevice = async (deviceId: string) => {
    if (!db.pb) {
      throw new Error('PocketBase client unavailable for device revoke.')
    }
    setIsRevokingDeviceId(deviceId)
    try {
      await db.pb.send('/api/kiosk/device/revoke', {
        method: 'POST',
        body: { deviceId },
      })
      setOptimisticallyRevokedDeviceIds((current) => (current.includes(deviceId) ? current : [...current, deviceId]))
      await refreshKioskPairedDevices(pairedDevices?.collection as { utils?: { refetch?: () => Promise<unknown> }; preload?: () => Promise<unknown> } | null | undefined)
    } finally {
      setIsRevokingDeviceId(null)
    }
  }

  return (
    <div className="relative isolate bg-slate-50 text-slate-900">
      {/* The check-out selector embeds the services ledger + side panel, which
          needs the same width the services page gets. */}
      <main className={`relative mx-auto flex flex-col gap-4 px-4 py-4 ${overviewMode === 'checkout-selector' ? 'max-w-[1720px]' : 'max-w-7xl'}`}>
        <header className="flex flex-col gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Kiosk control</h1>
            <p className="mt-2 text-sm text-slate-500">This view is locked to the customer check-in / check-out kiosk. Tablets themselves always live on `/kiosk`.</p>
            <nav className="mt-4 flex gap-1 border-b border-slate-200">
              <Link
                to="/kiosk-control/check-in-out"
                className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium ${
                  activeTab === 'check-in-out'
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Check In/Out
              </Link>
              <Link
                to="/kiosk-control/devices"
                className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium ${
                  activeTab === 'devices'
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                Devices
              </Link>
            </nav>
          </div>
        </header>

        {activeTab === 'check-in-out' ? (
          <>
            <div className="flex justify-end gap-3">
              {!isIdle && session.currentStep === KIOSK_STEP_TERMS ? (
                <button
                  type="button"
                  className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
                  onClick={() => {
                    setNoSignatureSubmitError(null)
                    setNoSignatureReason('')
                    setNoSignatureCheckedOutName(
                      (pendingPatchRef.current.checkedOutName ?? session.formData.checkedOutName ?? '').trim(),
                    )
                    setConfirmNoSignatureOpen(true)
                  }}
                >
                  {session.flow === 'check-out' ? 'Complete without signature' : 'Check in without signature'}
                </button>
              ) : null}
              <button
                type="button"
                className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"
                onClick={() => setConfirmResetOpen(true)}
              >
                Reset session
              </button>
            </div>
            {isIdle ? (
            hasCompletedCheckout ? (
              <KioskCheckoutFinishPanel
                kiosk={selectedKiosk}
                job={sessionJob}
                onReset={reset}
              />
            ) : hasCompletedCheckIn ? (
              <KioskJobCreatedPanel
                kiosk={selectedKiosk}
                session={session}
                onAcknowledge={reset}
                onAssignTag={assignTagAndStart}
              />
            ) : overviewMode === 'checkout-selector' ? (
              <KioskCheckoutSelectorPanel
                onBack={() => setOverviewMode('idle')}
                onStartCheckout={startCheckout}
              />
            ) : (
              <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                <PanelShell title={getKioskDisplayName(selectedKiosk)} subtitle="This mirrors the mounted tablet while it is idle.">
                  <KioskWelcomeScreen
                    label={selectedKiosk.label}
                    friendlyName={selectedKiosk.friendlyName}
                    sessionBadge="<id>"
                  />
                </PanelShell>
                <PanelShell title="Start customer flow" subtitle="Link a returning customer first if you have one, then launch the wizard." light>
                  <div className="space-y-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-slate-900 shadow-sm">
                      <AutocompleteCustomerContainer
                        value={customerOption}
                        onChange={linkCustomer}
                        ariaLabel="Link returning customer"
                        allowCreateOption={false}
                      />
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                      {session.formData.customerId ? 'Customer linked. Their details will be pre-filled for confirmation on the tablet.' : 'No customer linked yet. You can still start check-in and capture a new customer.'}
                    </div>
                    <div className="flex flex-wrap gap-3 pt-4">
                      <button type="button" className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white" onClick={startCheckIn}>
                        Start check-in
                      </button>
                      <button type="button" className="rounded-2xl border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700" onClick={() => setOverviewMode('checkout-selector')}>
                        Start check-out
                      </button>
                    </div>
                  </div>
                </PanelShell>
              </div>
            )
          ) : (
            <KioskWizardBody
              kiosk={selectedKiosk}
              session={session}
              terms={session.flow === 'check-out' ? checkoutTerms : terms}
              isTablet={false}
              sessionBadge="<id>"
              onPatch={patchSession}
              onStepChange={async (step) => {
                const mergedFormData = { ...session.formData, ...pendingPatchRef.current }
                await flushPendingPatch()
                await syncLinkedCustomerOnStep(db, session.currentStep, step, mergedFormData)
                await setKioskSessionStep(db, session.id, step as KioskStep)
              }}
              onSubmit={submitCheckout}
              onReset={reset}
            />
          )}
          </>
        ) : (
          <KioskPairDevicePanel
            kiosk={selectedKiosk}
            session={session}
            devices={pairedDeviceRows}
            isRevokingDeviceId={isRevokingDeviceId}
            onRefreshDevices={() => refreshKioskPairedDevices(pairedDevices?.collection as { utils?: { refetch?: () => Promise<unknown> }; preload?: () => Promise<unknown> } | null | undefined)}
            onRevokeDevice={revokeDevice}
          />
        )}
        <Dialog open={tagConflict !== null} onOpenChange={(open) => {
          if (!open) setTagConflict(null)
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Number tag already assigned</DialogTitle>
              <DialogDescription>
                {tagConflict?.message}
              </DialogDescription>
            </DialogHeader>
            {tagConflict?.conflictingJob ? (
              <div className="text-sm text-slate-600">
                Assigned job:{' '}
                <Link to="/jobs/$id" params={{ id: tagConflict.conflictingJob.id }} className="font-medium text-blue-700 hover:underline">
                  {tagConflict.conflictingJob.publicId ?? tagConflict.conflictingJob.id}
                </Link>
              </div>
            ) : null}
            <DialogFooter>
              <button
                type="button"
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                onClick={() => setTagConflict(null)}
              >
                Close
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={confirmNoSignatureOpen}
          onOpenChange={(open) => {
            setConfirmNoSignatureOpen(open)
            if (!open) {
              setNoSignatureReason('')
              setNoSignatureCheckedOutName('')
              setNoSignatureSubmitError(null)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{session.flow === 'check-out' ? 'Complete without signature?' : 'Check in without signature?'}</DialogTitle>
              <DialogDescription>
                Add a short reason so the office team knows why no signature was captured or needed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 pt-2">
              {session.flow === 'check-out' ? (
                <>
                  <FormCapsLabel>Collected by name (required)</FormCapsLabel>
                  <UnderlineInput
                    value={noSignatureCheckedOutName}
                    placeholder="Full name"
                    onChange={(event) => setNoSignatureCheckedOutName(event.target.value)}
                  />
                </>
              ) : null}
              <FormCapsLabel>Reason (required)</FormCapsLabel>
              <UnderlineTextarea
                rows={3}
                value={noSignatureReason}
                placeholder={session.flow === 'check-out'
                  ? 'Required. E.g. customer declined, remote approval on file, company account collection…'
                  : 'Required. E.g. customer unable to sign, verbal approval captured, assisted walk-in…'}
                onChange={(event) => setNoSignatureReason(event.target.value)}
              />
              {noSignatureSubmitError ? (
                <p className="text-sm text-rose-600">{noSignatureSubmitError}</p>
              ) : null}
            </div>
            <DialogFooter>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                disabled={isSubmittingNoSignature}
                onClick={() => {
                  setConfirmNoSignatureOpen(false)
                  setNoSignatureReason('')
                  setNoSignatureCheckedOutName('')
                  setNoSignatureSubmitError(null)
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={
                  !noSignatureReason.trim()
                  || (session.flow === 'check-out' && !noSignatureCheckedOutName.trim())
                  || isSubmittingNoSignature
                }
                onClick={() => void handleCompleteWithoutSignature()}
              >
                {isSubmittingNoSignature ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : null}
                {session.flow === 'check-out' ? 'Complete without signature' : 'Check in without signature'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={confirmResetOpen} onOpenChange={setConfirmResetOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset kiosk session?</DialogTitle>
              <DialogDescription>
                This will clear the current kiosk state and send the tablet back to idle.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                type="button"
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => setConfirmResetOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                onClick={() => {
                  setConfirmResetOpen(false)
                  void reset()
                }}
              >
                Reset
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  )
}

export function KioskTabletScreenContainer() {
  const db = useDb() as ReturnType<typeof useDb> & {
    kioskSessionsCollection?: { preload?: () => Promise<unknown> }
  }
  const pb = usePocketBase()
  const [authState, setAuthState] = useState<'checking' | 'ready'>(db.authStore.token ? 'checking' : 'ready')
  const [isPairing, setIsPairing] = useState(false)
  const [pairingError, setPairingError] = useState<string | null>(null)
  const [pairingState, setPairingState] = useState<null | { pairingId: string; code: string; expiresAt: string }>(null)
  const [isRecoveringSession, setIsRecoveringSession] = useState(false)
  const recoveryStartedRef = useRef(false)
  const pairingRequestStartedRef = useRef(false)
  const { kiosks, sessions, documents, pairedDevices } = useKioskData()
  const jobsResult = useLiveQuery((q) => q.from({ job: db.jobsCollection }))
  const assignmentsResult = useLiveQuery((q) => q.from({ assignments: db.jobAssignmentsCollection }))
  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const [showThankYou, setShowThankYou] = useState<'check-in' | 'check-out' | false>(false)

  const kioskRows = (kiosks.data ?? []) as Kiosk[]
  const sessionRows = (sessions.data ?? []) as KioskSession[]
  const documentRows = (documents.data ?? []) as Document[]
  const pairedDeviceRows = (pairedDevices?.data ?? []) as Array<{ deviceUuid: string; pairingCode?: string }>
  const authKioskId = db.authStore.user?.kioskId
  const authDeviceUuid = db.authStore.user?.deviceUuid
  const kiosk = kioskRows.find((entry) => entry.id === authKioskId)
  const session = kiosk ? getKioskSessionForKiosk(kiosk.id, sessionRows) : undefined
  const pairedDevice = authDeviceUuid
    ? pairedDeviceRows.find((entry) => entry.deviceUuid === authDeviceUuid)
    : undefined
  const deviceSessionBadge = pairedDevice?.pairingCode
  const terms = documentRows.find((document) => document.key === (session?.flow === 'check-out' ? CHECKOUT_TERMS_KEY : CHECKIN_TERMS_KEY))
  const usersById = new Map(((usersResult.data ?? []) as { id: string; name: string }[]).map((user) => [user.id, user.name]))
  const assignedUserNameByJobId = new Map<string, string>()
  for (const assignment of (assignmentsResult.data ?? []) as { jobId: string; userId: string; unassignedAt?: string }[]) {
    if (assignment.unassignedAt) continue
    if (assignedUserNameByJobId.has(assignment.jobId)) continue
    assignedUserNameByJobId.set(assignment.jobId, usersById.get(assignment.userId) ?? 'Assigned')
  }
  const patchTimeoutRef = useRef<number | null>(null)
  const pendingPatchRef = useRef<Partial<KioskSessionFormData>>({})
  const pairingRequested = authState === 'ready' && !db.authStore.isValid

  // Reloading must never interrupt a customer mid-flow; only the idle welcome
  // screen and the jobs board are safe. Kept in a ref so the heartbeat
  // interval below reads the latest state without re-subscribing.
  const reloadSafeRef = useRef(false)
  reloadSafeRef.current = Boolean(
    session &&
      (session.kioskType === 'jobs-board' || (session.flow === 'idle' && !showThankYou)),
  )

  // Validate the device session once on mount, then heartbeat every 120s.
  // The heartbeat keeps last_seen_at fresh and enforces revocation within
  // ~2 minutes. Deliberately NOT keyed on the token: each refresh issues a
  // new token, so a token-dependent effect re-fires forever.
  useEffect(() => {
    let cancelled = false

    const refresh = async () => {
      if (!db.authStore.token) return
      try {
        await db.refreshAuth(getOrCreateKioskDeviceUuid())
      } catch (error) {
        // Only un-pair when the server explicitly rejected the token or
        // device (expired/revoked). A network blip coinciding with a
        // heartbeat must not force re-pairing mid-shift (JC-095) — keep the
        // session and let the next heartbeat retry.
        if (!isAuthRejection(error)) {
          console.warn('Kiosk auth refresh failed transiently, will retry', error)
          return
        }
        console.warn('Kiosk auth rejected by server, clearing auth session', error)
        db.authStore.clear()
        if (!cancelled) {
          setPairingState(null)
        }
      }
    }

    const run = async () => {
      if (!db.authStore.token) {
        setAuthState('ready')
        return
      }

      setAuthState('checking')
      await refresh()
      if (!cancelled) {
        setAuthState('ready')
      }
    }

    const reloadIfNewVersion = async () => {
      if (!appVersion) return
      const servedVersion = await fetchServedAppVersion()
      if (!servedVersion || servedVersion === appVersion) return
      if (!reloadSafeRef.current) return
      window.location.reload()
    }

    void run()
    const interval = window.setInterval(() => {
      void refresh()
      void reloadIfNewVersion()
    }, 120_000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [db])

  useEffect(() => {
    return () => {
      if (patchTimeoutRef.current !== null) {
        window.clearTimeout(patchTimeoutRef.current)
      }
    }
  }, [])

  const flushPendingPatch = async () => {
    if (patchTimeoutRef.current !== null) {
      window.clearTimeout(patchTimeoutRef.current)
      patchTimeoutRef.current = null
    }

    const pendingPatch = pendingPatchRef.current
    if (Object.keys(pendingPatch).length === 0) return

    pendingPatchRef.current = {}
    if (!session) return
    await patchKioskSessionForm(db, session.id, pendingPatch)
  }

  useEffect(() => {
    if (showThankYou) {
      const timeout = window.setTimeout(() => setShowThankYou(false), THANK_YOU_DURATION_MS)
      return () => window.clearTimeout(timeout)
    }
  }, [showThankYou])

  useEffect(() => {
    if (authState !== 'ready' || !db.authStore.isValid || !pb || !kiosk || session || recoveryStartedRef.current) return

    recoveryStartedRef.current = true

    const recoverMissingSession = async () => {
      setIsRecoveringSession(true)
      try {
        await db.ensureKioskSessionExistsAndWait(kiosk.id, 'check-in')
      } catch (error) {
        console.warn('Failed to recover kiosk session', error)
      } finally {
        setIsRecoveringSession(false)
        recoveryStartedRef.current = false
      }
    }

    void recoverMissingSession()
  }, [authState, db, pb, kiosk, session, db.authStore.isValid])

  useEffect(() => {
    if (!pb || !pairingRequested || pairingState || db.authStore.isValid || pairingRequestStartedRef.current) return

    pairingRequestStartedRef.current = true

    const startPairing = async () => {
      setIsPairing(true)
      setPairingError(null)
      try {
        const url = pb.buildURL('/api/kiosk/pairing/start')
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ deviceUuid: getOrCreateKioskDeviceUuid() }),
        })

        const result = await response.json() as { pairingId?: string; code?: string; expiresAt?: string; error?: string }
        if (!response.ok) {
          throw new Error(result?.error || `Unable to start kiosk pairing (${response.status})`)
        }
        if (!result?.pairingId || !result?.code || !result?.expiresAt) {
          throw new Error('Pairing start returned incomplete data')
        }
        setPairingState({ pairingId: result.pairingId, code: result.code, expiresAt: result.expiresAt })
      } catch (error) {
        pairingRequestStartedRef.current = false
        const message = error instanceof Error ? error.message : 'Unable to start kiosk pairing.'
        setPairingError(message)
      } finally {
        setIsPairing(false)
      }
    }

    void startPairing()
  }, [pb, pairingRequested, pairingState, db.authStore.isValid])

  useEffect(() => {
    if (!pb || !pairingState || db.authStore.isValid) return

    let cancelled = false
    const expiresAtMs = new Date(pairingState.expiresAt).getTime()

    const poll = async () => {
      if (cancelled) return

      if (!Number.isFinite(expiresAtMs) || Date.now() >= expiresAtMs) {
        setPairingError('Pairing code expired. Reload this page to get a new code.')
        setPairingState(null)
        pairingRequestStartedRef.current = false
        return
      }

      try {
        const response = await pb.send('/api/kiosk/pairing/poll', {
          method: 'POST',
          body: {
            pairingId: pairingState.pairingId,
            code: pairingState.code,
            deviceUuid: getOrCreateKioskDeviceUuid(),
          },
        }) as { status?: string; token?: string; record?: Record<string, unknown> }

        if (cancelled) return

        if (typeof response?.token === 'string' && response.token.length > 0 && response.record) {
          pb.authStore.clear()
          pb.authStore.save(response.token, response.record as any)
          window.location.href = '/kiosk'
          return
        }

        if (response?.status === 'expired') {
          setPairingError('Pairing code expired. Reload this page to get a new code.')
          setPairingState(null)
          pairingRequestStartedRef.current = false
        }
      } catch (error) {
        if (cancelled) return
        const message = error instanceof Error ? error.message : 'Unable to poll kiosk pairing.'
        setPairingError(message)
      }
    }

    void poll()
    const interval = window.setInterval(() => {
      void poll()
    }, 5000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [pb, pairingState, db.authStore.isValid])

  if (kiosks.isLoading || sessions.isLoading || documents.isLoading || jobsResult.isLoading || assignmentsResult.isLoading || usersResult.isLoading || pairedDevices?.isLoading) {
    return <KioskLoadingState fullScreen />
  }

  if (kiosks.isError || sessions.isError || documents.isError || jobsResult.isError || assignmentsResult.isError || usersResult.isError || pairedDevices?.isError) {
    return <KioskErrorState fullScreen message="Unable to load kiosk right now." />
  }


  if (showThankYou === 'check-in') {
    return <KioskThankYouScreen />
  }

  if (showThankYou === 'check-out') {
    return <KioskCheckoutThankYouScreen />
  }

  if (authState === 'checking') {
    return <KioskLoadingState fullScreen />
  }

  if (!db.authStore.isValid) {
    if (pairingRequested) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-950 px-6 text-white">
          <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-slate-900/90 p-8 text-center shadow-2xl ring-1 ring-white/10 backdrop-blur">
            <p className="text-sm uppercase tracking-[0.25em] text-slate-300">Kiosk pairing</p>
            <div className="mt-4 rounded-2xl border border-cyan-300/30 bg-black/40 px-6 py-5 ring-1 ring-cyan-300/10">
              <h1 className="font-mono text-6xl font-bold tracking-[0.3em] text-cyan-200">{pairingState?.code || '......'}</h1>
            </div>
            <p className="mt-4 text-sm text-slate-200">Log in on a staff device, then enter this code to authorise this tablet.</p>
            {isPairing ? <p className="mt-4 text-sm text-slate-400">Generating code…</p> : null}
            {pairingError ? <p className="mt-4 text-sm text-rose-300">{pairingError}</p> : null}
          </div>
        </div>
      )
    }

    return <KioskErrorState fullScreen message="This device has no kiosk assignment yet. Open a kiosk-specific setup link once, then it can live on /kiosk." />
  }

  if (!kiosk) {
    return <KioskErrorState fullScreen message="This paired device does not match any registered kiosk. Re-pair the device if its kiosk assignment changed." />
  }

  if (!session) {
    if (isRecoveringSession) {
      return <KioskLoadingState fullScreen />
    }

    return <KioskErrorState fullScreen message="This kiosk is missing its session record. Reload if this was just paired." />
  }

  if (session.kioskType === 'jobs-board') {
    return (
      <div className="absolute inset-0 overflow-auto bg-slate-950 p-5 text-white">
        <KioskJobsBoardPanel
          kiosk={kiosk}
          jobs={(jobsResult.data ?? []) as Job[]}
          assignedUserNameByJobId={assignedUserNameByJobId}
          compact
        />
      </div>
    )
  }

  if (session.flow === 'idle') {
    return (
      <KioskWelcomeScreen
        fullScreen
        label={kiosk.label}
        friendlyName={kiosk.friendlyName}
        sessionBadge={deviceSessionBadge}
      />
    )
  }

  const patchSession = async (patch: Partial<KioskSessionFormData>) => {
    pendingPatchRef.current = {
      ...pendingPatchRef.current,
      ...patch,
    }

    if (patchTimeoutRef.current !== null) {
      window.clearTimeout(patchTimeoutRef.current)
    }

    patchTimeoutRef.current = window.setTimeout(() => {
      void flushPendingPatch()
    }, KIOSK_FORM_SYNC_DEBOUNCE_MS)
  }

  const submit = async (submission: KioskSignatureSubmission) => {
    await flushPendingPatch()
    if (!pb) {
      throw new Error('PocketBase client unavailable for kiosk submission')
    }

    if (session.flow === 'check-out') {
      if (!session.jobId) {
        throw new Error('No job selected for kiosk check-out')
      }

      if (!submission.formData.checkedOutName?.trim()) {
        throw new Error('Collected by name is required')
      }

      const noSignatureReason = submission.noSignatureReason?.trim() ?? ''
      const useNoSignatureReason = Boolean(submission.useNoSignatureReason)
      if (useNoSignatureReason && !noSignatureReason) {
        throw new Error('Reason no signature was captured is required')
      }
      if (!useNoSignatureReason && !submission.signatureDataUrl) {
        throw new Error('Signature is required')
      }

      await db.completeKioskCheckoutAndWait({
        jobId: String(session.jobId),
        checkedOutName: submission.formData.checkedOutName,
        checkedOutPhone: submission.formData.checkedOutPhone,
        processedBy: session.startedBy,
        noSignatureReason: useNoSignatureReason ? noSignatureReason : undefined,
        signatureFile: useNoSignatureReason
          ? undefined
          : dataUrlToFile(submission.signatureDataUrl!, `checkout-signature-${Date.now()}.png`),
      })

      await db.updateKioskSessionWithLatestAndWait(session.id, () => ({
        flow: 'idle',
        currentStep: KIOSK_STEP_CUSTOMER,
        formData: {},
        jobId: session.jobId,
        lastCompletedFlow: 'check-out',
        startedBy: undefined,
      }))

      setShowThankYou('check-out')
      return
    }

    const customerFields = buildCustomerPatch(submission.formData)
    let customerId = submission.formData.customerId

    if (customerId) {
      await db.upsertKioskCustomerAndWait(customerId, customerFields)
    } else {
      const customer = await db.createCustomerAndWait({
        fullName: customerFields.fullName,
        contactPhone: customerFields.contactPhone,
        contactEmail: customerFields.contactEmail,
        contactAddress: customerFields.contactAddress,
        invoiceName: customerFields.invoiceName,
        invoiceAddress: customerFields.invoiceAddress,
        invoiceVat: customerFields.invoiceVat,
        invoiceEmail: customerFields.invoiceEmail,
        invoicePhone: customerFields.invoicePhone,
      })
      customerId = customer.id
    }

    const noSignatureReason = submission.noSignatureReason?.trim() ?? ''
    const useNoSignatureReason = Boolean(submission.useNoSignatureReason)
    if (useNoSignatureReason && !noSignatureReason) {
      throw new Error('Reason no signature was captured is required')
    }
    if (!useNoSignatureReason && !submission.signatureDataUrl) {
      throw new Error('Signature is required')
    }

    const jobRecord = await db.createKioskJobAndWait({
      customerId,
      formData: submission.formData,
      processedBy: session.startedBy,
      noSignatureReason: useNoSignatureReason ? noSignatureReason : undefined,
      signatureFile: useNoSignatureReason
        ? undefined
        : dataUrlToFile(submission.signatureDataUrl!, `checkin-signature-${Date.now()}.png`),
    })

    await db.updateKioskSessionWithLatestAndWait(session.id, () => ({
      flow: 'idle',
      currentStep: KIOSK_STEP_CUSTOMER,
      formData: {},
      jobId: String(jobRecord.id),
      lastCompletedFlow: 'check-in',
      startedBy: undefined,
    }))

    setShowThankYou('check-in')
  }

  return (
    <KioskWizardBody
      kiosk={kiosk}
      session={session}
      terms={terms}
      isTablet
      sessionBadge={deviceSessionBadge}
      onPatch={patchSession}
      onStepChange={async (step) => {
        const mergedFormData = { ...session.formData, ...pendingPatchRef.current }
        await flushPendingPatch()
        await syncLinkedCustomerOnStep(db, session.currentStep, step, mergedFormData)
        await setKioskSessionStep(db, session.id, step as KioskStep)
      }}
      onSubmit={submit}
    />
  )
}
